import React from 'react';

import { businessDotColor } from '../../utils/business';

// Retratos de los IBOs. Encuadres muy dispares (estudio, pareja,
// eventos con logos de fondo): un círculo pequeño tolera cualquiera
// sin dejar la card desbalanceada.
import testiPic1 from '../../images/testimonials/testpic1.jpg';
import testiPic2 from '../../images/testimonials/testpic2.jpg';
import testiPic3 from '../../images/testimonials/testpic3.jpg';
import testiPic4 from '../../images/testimonials/testpic4.jpg';

/**
 * Testimonios del home. Rediseño:
 *  - La cita es el elemento principal (tipografía ~18px, comillas
 *    integradas en el propio texto, sin badge flotante).
 *  - Pie con foto circular 56 px + nombre + chip de línea de negocio
 *    (usa `businessDotColor` para que el punto coincida con el color
 *    canónico del negocio — el mismo que gastan `LearningCard` y
 *    `HomeBusinessGrid`).
 *  - Fondo único neutro. Los cuatro fondos pastel previos
 *    pretendían codificar la línea de negocio pero: (a) Real Vision
 *    y Vision Travel compartían color, y (b) los tonos no coincidían
 *    con `utils/business.js`. Como código quedaban rotos; como
 *    decoración distraían del testimonio.
 *  - Layout `flex-column` con la cita `flex: 1` — altura pareja
 *    entre citas de distinta longitud, el pie se ancla abajo.
 */
const testimonios = [
  {
    thumb: testiPic1,
    name: 'Keila Montoya',
    business: 'Vision Travel',
    quote:
      'Soy afortunada de tener mi negocio productivo de turismo y crecer junto a mi equipo.',
  },
  {
    thumb: testiPic2,
    name: 'Edgar y Geimaru Pino',
    business: 'VisionBiz',
    quote:
      'Cuando descubres que aquí puedes lograr tus sueños en pareja, se vuelve algo poderoso.',
  },
  {
    thumb: testiPic3,
    name: 'Alicia Phillips',
    business: 'Safeguard',
    quote:
      'Solo VisionBiz me permite ganar dinero con turismo, inmuebles y seguros, desde mi casa.',
  },
  {
    thumb: testiPic4,
    name: 'Dayana Martínez',
    business: 'Real Vision',
    quote:
      'Agradezco a Dios cada día por VisionBiz, que me permite crecer, ganar dinero y tener libertad financiera.',
  },
];

const Testimonial2 = () => (
  <section aria-label="Testimonios de nuestros IBO">
    {/* En >=1024px se ven los 4 en una fila (grid). Por debajo se
        convierte en carrusel horizontal con scroll-snap nativo: una
        tarjeta ocupa ~85% del ancho para dejar "peek" de la siguiente
        y comunicar que hay más. Sin dependencias JS. */}
    <style>{`
      [data-slot="testimonials-grid"] {
        display: flex;
        gap: 16px;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        scroll-padding-inline: 4px;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        padding-bottom: 4px;
      }
      [data-slot="testimonials-grid"]::-webkit-scrollbar {
        display: none;
      }
      [data-slot="testimonials-grid"] > article {
        flex: 0 0 85%;
        scroll-snap-align: start;
      }
      @media (min-width: 640px) {
        [data-slot="testimonials-grid"] > article {
          flex: 0 0 60%;
        }
      }
      @media (min-width: 1024px) {
        [data-slot="testimonials-grid"] {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          overflow: visible;
          scroll-snap-type: none;
          padding-bottom: 0;
        }
        [data-slot="testimonials-grid"] > article {
          flex: initial;
        }
      }
    `}</style>
    <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 16px' }}>
      Lo que dicen nuestros IBO
    </h2>
    <div data-slot="testimonials-grid">
      {testimonios.map((tst, i) => {
        const dot = businessDotColor(tst.business);
        return (
          <article
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '24px 22px 22px',
              borderRadius: 18,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-line)',
              height: 'auto',
            }}
          >
            <blockquote
              style={{
                margin: 0,
                flex: 1,
                fontSize: 17,
                lineHeight: 1.55,
                fontWeight: 600,
                color: 'var(--color-ink)',
              }}
            >
              {`“${tst.quote}”`}
            </blockquote>

            <footer
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginTop: 20,
                paddingTop: 16,
                borderTop: '1px solid var(--color-line)',
              }}
            >
              <img
                src={tst.thumb}
                alt=""
                loading="lazy"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  flex: '0 0 auto',
                  display: 'block',
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 800,
                    color: 'var(--color-ink)',
                    lineHeight: 1.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tst.name}
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 4,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: 'var(--color-ink-2)',
                    letterSpacing: '.01em',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: dot,
                      flex: '0 0 auto',
                    }}
                  />
                  {tst.business}
                </div>
              </div>
            </footer>
          </article>
        );
      })}
    </div>
  </section>
);

export default Testimonial2;
